import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import CustomPaginator from '../../components/CustomPaginator';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import SearchFilterBar from '../../components/SearchFilterBar';
import FeesPanelSkeletonLoader from '../../skeletons/FeesPanelSkeletonLoader';
import DeleteFeeModal from './parts/DeleteFeeModal';
import MarkFeeModal from './parts/MarkFeeModal';
import StudentFeePanel from './parts/StudentFeePanel';
import { useOptions } from '../../stateManagers/hooks/useOptions';
import ProfilePictureHover from '../../components/ProfilePictureHover';

interface FeeRecord {
  student_id: number;
  user_id: number | null;
  profile_picture_url: string | null;
  student_name: string;
  class: string;
  section: string | null;
  roll_no: number;
  fee_record_id: number | null;
  session: string;
  month_year: string;
  receipt_no: string | null;
  paid_date: string | null;
  total_amount: string | null;
  paid_amount: string | null;
  fees_status: string;
  pay_mode: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface PaginationData {
  current_page: number;
  per_page: number;
  total_items: number;
  total_pages: number;
}

interface CountsData {
  total_students: number;
  paid_students: number;
  unpaid_students: number;
}

interface FilterState {
  selectedClass: string;
  selectedSection: string;
  selectedMonth: string;
  selectedStatus: string;
}

const isFilterActive = (filters: FilterState): boolean =>
  filters.selectedClass.trim() !== '' ||
  filters.selectedSection.trim() !== '' ||
  filters.selectedMonth.trim() !== '' ||
  (filters.selectedStatus.trim() !== '' && filters.selectedStatus.toLowerCase() !== 'all');

const getRowKey = (record: FeeRecord): string =>
  record.fee_record_id !== null && record.fee_record_id !== undefined
    ? `fee-${record.fee_record_id}`
    : `student-${record.student_id}`;

// ─── Avatar ────────────────────────────────────────────────────────────────
const StudentAvatar: React.FC<{ name: string; pictureUrl: string | null }> = ({ name, pictureUrl }) => {
  const [imgError, setImgError] = useState(false);
  if (pictureUrl && !imgError) {
    return (
      <img
        src={pictureUrl} alt={name} onError={() => setImgError(true)}
        style={{ width: 38, height: 38, borderRadius: 10, objectFit: 'cover', flexShrink: 0, border: '2px solid #e5e7eb' }}
      />
    );
  }
  return (
    <div style={{
      width: 38, height: 38, borderRadius: 10, flexShrink: 0,
      background: 'linear-gradient(135deg, #4169E1 0%, #5B7CE6 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'white', fontWeight: 600, fontSize: 14,
    }}>
      {name.charAt(0).toUpperCase()}
    </div>
  );
};

// ─── Month list builder ────────────────────────────────────────────────────
const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

export const buildSessionMonths = (startMonth: string, endMonth: string): string[] => {
  const startIdx = MONTH_NAMES.findIndex(m => m.toLowerCase() === startMonth.toLowerCase());
  const endIdx   = MONTH_NAMES.findIndex(m => m.toLowerCase() === endMonth.toLowerCase());
  if (startIdx === -1 || endIdx === -1) return MONTH_NAMES;
  const months: string[] = [];
  let i = startIdx;
  while (true) {
    months.push(MONTH_NAMES[i]);
    if (i === endIdx) break;
    i = (i + 1) % 12;
  }
  return months;
};

/**
 * Returns a map of month name (lowercase) → correct 4-digit year string,
 * respecting the year boundary in cross-year sessions.
 * e.g. session "2026-2027", start "November", end "October"
 * → { november: "2026", december: "2026", january: "2027", ..., october: "2027" }
 */
export const buildSessionMonthYearMap = (
  startMonth: string,
  endMonth: string,
  session: string,
): Record<string, string> => {
  const months = buildSessionMonths(startMonth, endMonth);
  const years  = session.split('-');
  const yearA  = years[0] ?? String(new Date().getFullYear());
  const yearB  = years[1] ?? yearA;

  const indices = months.map(m =>
    MONTH_NAMES.findIndex(mn => mn.toLowerCase() === m.toLowerCase())
  );

  const map: Record<string, string> = {};
  let wrapped = false;
  months.forEach((name, i) => {
    if (i > 0 && indices[i] < indices[i - 1]) wrapped = true;
    map[name.toLowerCase()] = wrapped ? yearB : yearA;
  });
  return map;
};

// ───────────────────────────────────────────────────────────────────────────
const FeesPanel: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [feeRecords,     setFeeRecords]     = useState<FeeRecord[]>([]);
  const [loading,        setLoading]        = useState(false);
  const [page,           setPage]           = useState(1);

  // ── Fetch session options (single call for all three) ────────────────────
  const { options: sessionOptions, loading: sessionLoading } = useOptions<string[]>([
    'current_session',
    'session_duration_start',
    'session_duration_end',
  ]);

  const SESSION       = sessionOptions['current_session']?.data?.[0]       ?? null;
  const SESSION_START = sessionOptions['session_duration_start']?.data?.[0] ?? null;
  const SESSION_END   = sessionOptions['session_duration_end']?.data?.[0]   ?? null;

  console.log('values are', SESSION,SESSION_START,SESSION_END)

  // Build the ordered month list  active session
  const sessionMonths = SESSION_START && SESSION_END
    ? buildSessionMonths(SESSION_START, SESSION_END)
    : MONTH_NAMES;

  // month-name → correct year for this session
  const sessionMonthYearMap: Record<string, string> =
    SESSION_START && SESSION_END && SESSION
      ? buildSessionMonthYearMap(SESSION_START, SESSION_END, SESSION)
      : Object.fromEntries(MONTH_NAMES.map(m => [m.toLowerCase(), String(new Date().getFullYear())]));

  // Side panel state
  const [panelStudentId,     setPanelStudentId]     = useState<number | null>(null);
  const [panelStudentRecord, setPanelStudentRecord] = useState<FeeRecord | null>(null);

  // Modals
  const [isDeleteModalOpen,   setIsDeleteModalOpen]   = useState(false);
  const [isMarkFeeModalOpen,  setIsMarkFeeModalOpen]  = useState(false);
  const [selectedFeeRecordId, setSelectedFeeRecordId] = useState<number | null>(null);
  const [selectedFeeRecord,   setSelectedFeeRecord]   = useState<FeeRecord | null>(null);
  const [selectedStudentId,   setSelectedStudentId]   = useState<string | null>(null);
  const [selectedSession,     setSelectedSession]     = useState<string | null>(null);
  const [selectedMonthYear,   setSelectedMonthYear]   = useState<string | null>(null);

  const [paginationData, setPaginationData] = useState<PaginationData>({
    current_page: 1, per_page: 10, total_items: 0, total_pages: 1,
  });
  const [counts, setCounts] = useState<CountsData>({
    total_students: 0, paid_students: 0, unpaid_students: 0,
  });
  const [filters, setFilters] = useState<FilterState>({
    selectedClass: '', selectedSection: '', selectedMonth: '', selectedStatus: 'All',
  });

  const panelOpen = panelStudentId !== null;

  // Wait for SESSION to be ready before first fetch
  useEffect(() => {
    if (!SESSION) return;
    fetchFeeRecords(1, filters);
  }, [SESSION]);

  const fetchFeeRecords = useCallback(async (currentPage: number, currentFilters: FilterState) => {
    if (!SESSION) return;
    setLoading(true);
    try {
      const qp: string[] = [`page=${currentPage}`, `limit=10`, `session=${SESSION}`];
      const hasFilters = isFilterActive(currentFilters);

      // ── Default = first month of session with correct year ──────────────
      const defaultMonthYear = SESSION_START
        ? formatMonthYear(
            SESSION_START,
            sessionMonthYearMap[SESSION_START.toLowerCase()] ?? SESSION.split('-')[0]
          )
        : `${new Date().getFullYear()}-11`;

      if (!hasFilters) {
        qp.push(`month_year=${defaultMonthYear}`, 'last_paid=true');
      } else {
        if (currentFilters.selectedClass.trim()) {
          const cls = await getClassIdByName(currentFilters.selectedClass.toLowerCase().trim());
          if (cls?.id) qp.push(`class_id=${cls.id}`);
        }
        if (currentFilters.selectedSection.trim())
          qp.push(`section=${currentFilters.selectedSection.toLowerCase().trim()}`);

        if (currentFilters.selectedMonth.trim()) {
          // selectedMonth arrives as "November 2026" — split correctly
          const parts  = currentFilters.selectedMonth.trim().split(' ');
          const mnName = parts[0] ?? '';
          // Use the year from the filter value directly (SearchFilterBar now sends correct year)
          const yr     = parts[1] ?? sessionMonthYearMap[mnName.toLowerCase()] ?? SESSION.split('-')[0];
          const my     = formatMonthYear(mnName, yr);
          qp.push(my ? `month_year=${my}` : `month_year=${defaultMonthYear}`);
        } else {
          qp.push(`month_year=${defaultMonthYear}`);
        }

        const sl = currentFilters.selectedStatus.toLowerCase().trim();
        qp.push(`payment_status=${!sl || sl === 'all' ? 'all' : sl}`);
      }

      const response = await apiClient.get(`students/fees-records?${qp.join('&')}`);

      if (response.data.status === 'success') {
        setFeeRecords(response.data.data || []);
        setPaginationData(response.data.pagination || {
          current_page: currentPage, per_page: 10,
          total_items: response.data.data?.length || 0,
          total_pages: Math.ceil((response.data.data?.length || 0) / 10),
        });

        console.log(response.data)
        setCounts(response.data.counts || { total_students: 0, paid_students: 0, unpaid_students: 0 });
      } else {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: response.data.message || 'Failed to fetch fee records', life: 5000 });
      }
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.message || 'Failed to fetch fee records', life: 5000 });
    } finally {
      setLoading(false);
    }
  }, [SESSION, SESSION_START, sessionMonthYearMap]);

  const getClassIdByName = async (className: string) => {
    try {
      const res = await apiClient.get('/classes?page=1&limit=50');
      if (res.data.status === 'success')
        return res.data.classes.find((c: any) => c.class_name.toLowerCase().trim() === className);
      return null;
    } catch { return null; }
  };

  const formatMonthYear = (monthName: string, year: string): string => {
    const map: Record<string, string> = {
      january:'01', february:'02', march:'03', april:'04', may:'05', june:'06',
      july:'07', august:'08', september:'09', october:'10', november:'11', december:'12',
    };
    const code = map[monthName.toLowerCase().trim()];
    return code ? `${year}-${code}` : '';
  };

  // ── Panel open/close ──────────────────────────────────────────────────────
  const handleViewStudent = (record: FeeRecord) => {
    if (panelStudentId === record.student_id) {
      setPanelStudentId(null);
      setPanelStudentRecord(null);
    } else {
      setPanelStudentId(record.student_id);
      setPanelStudentRecord(record);
    }
  };

  const handleMarkPayment = (record: FeeRecord) => {
    setSelectedStudentId(record.student_id.toString());
    setSelectedSession(record.session);
    setSelectedMonthYear(record.month_year);
    setIsMarkFeeModalOpen(true);
  };

  const handleDeleteClick = (record: FeeRecord) => {
    if (!record.fee_record_id) return;
    setSelectedFeeRecordId(record.fee_record_id);
    setSelectedFeeRecord(record);
    setIsDeleteModalOpen(true);
  };

  const handleApplyFilters = (newFilters: FilterState) => {
    setFilters(newFilters);
    setPage(1);
    fetchFeeRecords(1, newFilters);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= paginationData.total_pages && newPage !== page) {
      setPage(newPage);
      fetchFeeRecords(newPage, filters);
    }
  };

  const isLastPaidMode = !isFilterActive(filters);

  // Show skeleton while session options are loading
  if (sessionLoading) {
    return <FeesPanelSkeletonLoader />;
  }

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <style>{`
        .fees-panel-container {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          min-height: 100vh;
        }
        .fees-content {
          background: white; border-radius: 20px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06);
          border: 1px solid #e5e7eb; overflow: hidden;
        }
        .fees-table-container { overflow-x: auto; }
        .fees-table { width: 100%; border-collapse: collapse; }
        .fees-table thead {
          background: linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%);
          border-bottom: 2px solid #e5e7eb;
        }
        .fees-table th {
          padding: 16px 20px; text-align: left;
          font-size: 11px; font-weight: 700; color: #6b7280;
          text-transform: uppercase; letter-spacing: 0.8px;
        }
        .fees-table tbody tr { border-bottom: 1px solid #f3f4f6; transition: all 0.2s; }
        .fees-table tbody tr:hover {
          background: linear-gradient(to right, rgba(65,105,225,0.02) 0%, rgba(65,105,225,0.01) 100%);
        }
        .fees-table tbody tr.row--active {
          background: rgba(65,105,225,0.05) !important;
        }
        .fees-table tbody tr:last-child { border-bottom: none; }
        .fees-table td { padding: 16px 20px; font-size: 13px; color: #1f2937; font-weight: 500; }
        .student-info { display: flex; align-items: center; gap: 12px; }
        .student-details { display: flex; flex-direction: column; gap: 2px; }
        .student-name { font-weight: 600; color: #1f2937; font-size: 13px; }
        .student-meta { font-size: 11px; color: #9ca3af; font-weight: 500; }
        .info-with-icon { display: flex; align-items: center; gap: 8px; }
        .status-badge {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 6px 12px; border-radius: 20px;
          font-size: 11px; font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.5px;
        }
        .status-badge.paid {
          background: linear-gradient(135deg, rgba(16,185,129,0.1), rgba(5,150,105,0.08));
          color: #059669; border: 1px solid rgba(16,185,129,0.2);
        }
        .status-badge.unpaid {
          background: linear-gradient(135deg, rgba(239,68,68,0.1), rgba(220,38,38,0.08));
          color: #dc2626; border: 1px solid rgba(239,68,68,0.2);
        }
        .status-badge.partial {
          background: linear-gradient(135deg, rgba(245,158,11,0.1), rgba(217,119,7,0.08));
          color: #d97706; border: 1px solid rgba(245,158,11,0.2);
        }
        .empty-state { padding: 80px 20px; text-align: center; }
        .empty-state-icon {
          width: 80px; height: 80px; margin: 0 auto 20px; border-radius: 50%;
          background: linear-gradient(135deg, rgba(65,105,225,0.1), rgba(91,124,230,0.05));
          display: flex; align-items: center; justify-content: center;
        }
        .empty-state h3 { margin: 0 0 8px; font-size: 18px; font-weight: 700; color: #1f2937; }
        .empty-state p  { margin: 0; font-size: 14px; color: #9ca3af; font-weight: 500; }
        .p-button-text:hover { background: #f3f4f6 !important; }
      `}</style>

      <div className="fees-panel-container">
        <GlobalHeaderTitle
          title="Fees Management"
          subtitle={`Manage and track all student fee records for session ${SESSION ?? '—'}`}
          icon="pi pi-dollar"
          gradientFrom="#4169E1"
          gradientTo="#5B7CE6"
        />

        {/* Filter bar */}
        <div style={{
          background: 'white', borderRadius: 20, padding: 20, marginBottom: 24,
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb',
          marginRight: panelOpen ? 436 : 0,
          transition: 'margin-right 0.28s cubic-bezier(0.4,0,0.2,1)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 20, flexWrap: 'wrap', marginBottom: 16 }}>

            {/* Session info pills */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Session badge */}
              {SESSION && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '6px 14px', borderRadius: 10,
                  background: 'rgba(65,105,225,0.08)', border: '1px solid rgba(65,105,225,0.2)',
                }}>
                  <i className="pi pi-calendar" style={{ fontSize: 13, color: '#4169E1' }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#1e40af' }}>
                    Session: {SESSION}
                  </span>
                </div>
              )}

              {/* Duration badge */}
              {SESSION_START && SESSION_END && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '6px 14px', borderRadius: 10,
                  background: 'rgba(99,102,241,0.07)', border: '1px solid rgba(99,102,241,0.18)',
                }}>
                  <i className="pi pi-clock" style={{ fontSize: 13, color: '#6366f1' }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#4338ca' }}>
                    {SESSION_START} – {SESSION_END}
                  </span>
                </div>
              )}
            </div>

            {/* Counts */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {[
                { count: counts.total_students,  label: 'Total',  icon: 'pi-users',        color: '#2563eb', boldColor: '#1e40af', bg: 'rgba(59,130,246,0.08)',  border: 'rgba(59,130,246,0.2)'  },
                { count: counts.paid_students,   label: 'Paid',   icon: 'pi-check-circle', color: '#059669', boldColor: '#047857', bg: 'rgba(16,185,129,0.08)',  border: 'rgba(16,185,129,0.2)'  },
                { count: counts.unpaid_students, label: 'Unpaid', icon: 'pi-times-circle', color: '#dc2626', boldColor: '#b91c1c', bg: 'rgba(239,68,68,0.08)',   border: 'rgba(239,68,68,0.2)'   },
              ].map(({ count, label, icon, color, boldColor, bg, border }) => (
                <div key={label} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px',
                  borderRadius: 10, background: `linear-gradient(135deg, ${bg} 0%, ${bg} 100%)`,
                  border: `1px solid ${border}`,
                }}>
                  <i className={`pi ${icon}`} style={{ fontSize: 16, color }} />
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{ fontSize: 20, fontWeight: 700, color: boldColor }}>{count}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>{label}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <SearchFilterBar
            onApplyFilters={handleApplyFilters}
            sessionMonths={sessionMonths}
            session={SESSION}
          />
        </div>

        {/* Table */}
        <div
          className="fees-content"
          style={{
            marginRight: panelOpen ? 436 : 0,
            transition: 'margin-right 0.28s cubic-bezier(0.4,0,0.2,1)',
          }}
        >
          {loading ? (
            <FeesPanelSkeletonLoader />
          ) : feeRecords.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <i className="pi pi-inbox" style={{ fontSize: 32, color: '#4169E1' }} />
              </div>
              <h3>No Fee Records Found</h3>
              <p>There are no fee records available for the selected filters.</p>
            </div>
          ) : (
            <>
              <div className="fees-table-container">
                <table className="fees-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Class & Section</th>
                      <th>Receipt No</th>
                      <th>Paid Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Payment Mode</th>
                      <th style={{ textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feeRecords.map((record) => (
                      <tr
                        key={getRowKey(record)}
                        className={panelStudentId === record.student_id ? 'row--active' : ''}
                      >
                        <td>
                          <div className="student-info">
                            {/* <StudentAvatar name={record.student_name} pictureUrl={record.profile_picture_url} /> */}
                            <ProfilePictureHover
                              src={record.profile_picture_url}
                              name={record.student_name}
                              size={38}
                              previewSize={240}
                              borderRadius={10}
                            />
                            <div className="student-details">
                              <span className="student-name">{record.student_name}</span>
                              <span className="student-meta">Roll: {record.roll_no}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{record.class}</div>
                          {record.section && <div style={{ fontSize: 11, color: '#9ca3af' }}>Section {record.section}</div>}
                        </td>
                        <td>
                          <div className="info-with-icon">
                            <i className="pi pi-file" style={{ fontSize: 14, color: '#9ca3af' }} />
                            {record.receipt_no ?? '—'}
                          </div>
                        </td>
                        <td>
                          {record.fees_status.toLowerCase() === 'paid' && record.paid_date ? (
                            <div className="info-with-icon">
                              <i className="pi pi-calendar" style={{ fontSize: 14, color: '#9ca3af' }} />
                              {new Date(record.paid_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </div>
                          ) : <span style={{ color: '#9ca3af' }}>—</span>}
                        </td>
                        <td>
                          {record.fees_status.toLowerCase() === 'paid' && record.paid_amount ? (
                            <div className="info-with-icon" style={{ fontWeight: 600 }}>
                              <i className="pi pi-indian-rupee" style={{ fontSize: 12, color: '#059669' }} />
                              {Number(record.paid_amount).toLocaleString('en-IN')}
                            </div>
                          ) : <span style={{ color: '#9ca3af' }}>—</span>}
                        </td>
                        <td>
                          <span className={`status-badge ${record.fees_status.toLowerCase()}`}>
                            <i
                              className={`pi ${record.fees_status.toLowerCase() === 'paid' ? 'pi-check-circle' : 'pi-times-circle'}`}
                              style={{ fontSize: 10 }}
                            />
                            {record.fees_status}
                          </span>
                        </td>
                        <td>
                          <div className="info-with-icon">
                            <i className="pi pi-credit-card" style={{ fontSize: 14, color: '#9ca3af' }} />
                            {record.pay_mode?.toUpperCase() ?? 'N/A'}
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                            <Button
                              icon="pi pi-eye"
                              className="p-button-text p-button-rounded"
                              tooltip={panelStudentId === record.student_id ? 'Close Panel' : 'View Full Details'}
                              tooltipOptions={{ position: 'top' }}
                              style={{
                                color: panelStudentId === record.student_id ? '#4169E1' : '#60a5fa',
                                width: 32, height: 32,
                                background: panelStudentId === record.student_id ? 'rgba(65,105,225,0.1)' : 'transparent',
                                borderRadius: 8,
                              }}
                              onClick={() => handleViewStudent(record)}
                            />
                            <Button
                              icon="pi pi-indian-rupee"
                              className="p-button-text p-button-rounded"
                              tooltip="Mark Payment"
                              tooltipOptions={{ position: 'top' }}
                              style={{ color: '#10b981', width: 32, height: 32 }}
                              onClick={() => handleMarkPayment(record)}
                            />
                            {record.fees_status.toLowerCase() === 'paid' && (
                              <Button
                                icon="pi pi-trash"
                                className="p-button-text p-button-rounded"
                                tooltip="Delete"
                                tooltipOptions={{ position: 'top' }}
                                style={{ color: '#ef4444', width: 32, height: 32 }}
                                onClick={() => handleDeleteClick(record)}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {!isLastPaidMode && paginationData.total_pages > 1 && (
                <CustomPaginator
                  page={paginationData.current_page}
                  totalPages={paginationData.total_pages}
                  totalRecords={paginationData.total_items}
                  pageSize={paginationData.per_page}
                  onPageChange={handlePageChange}
                  showInfoBar={true}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Student fee detail panel ── */}
      {panelOpen && (
        <StudentFeePanel
          studentId={panelStudentId}
          session={SESSION ?? ''}
          sessionStart={SESSION_START ?? ''}
          sessionEnd={SESSION_END ?? ''}
          studentRecord={panelStudentRecord ? {
            student_name:        panelStudentRecord.student_name,
            profile_picture_url: panelStudentRecord.profile_picture_url,
            class:               panelStudentRecord.class,
            section:             panelStudentRecord.section,
            roll_no:             panelStudentRecord.roll_no,
          } : null}
          onClose={() => {
            setPanelStudentId(null);
            setPanelStudentRecord(null);
          }}
          onPaymentSuccess={() => fetchFeeRecords(page, filters)}
        />
      )}

      {/* Modals */}
      <DeleteFeeModal
        visible={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setSelectedFeeRecordId(null); setSelectedFeeRecord(null); }}
        feeRecordId={selectedFeeRecordId}
        feeRecord={selectedFeeRecord}
        onSuccess={() => fetchFeeRecords(page, filters)}
        toast={toast}
      />
      <MarkFeeModal
        open={isMarkFeeModalOpen}
        onClose={() => { setIsMarkFeeModalOpen(false); setSelectedStudentId(null); setSelectedSession(null); setSelectedMonthYear(null); }}
        studentId={selectedStudentId}
        session={selectedSession}
        monthYear={selectedMonthYear}
        onSuccess={() => fetchFeeRecords(page, filters)}
        toast={toast}
      />
    </>
  );
};

export default FeesPanel;